'use client';

import { useFormatter, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type {
  PriceAlertDeliveryStatus,
  PriceAlertFailureReason,
  PriceAlertTriggerDto,
} from '@/lib/api/priceAlerts';
import { useTelegramConnect } from '@/lib/hooks/useTelegramConnect';
import { Link } from '@/lib/routing';
import { Icon, type IconName } from '@/lib/ui-kit/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { cn } from '@/lib/ui-kit/utils';
import { Z_OVERLAY_MAX } from './constants';

interface PriceAlertHistoryRowProps {
  trigger: PriceAlertTriggerDto;
}

const STATUS_PILL_BG: Record<PriceAlertDeliveryStatus, string> = {
  DELIVERED: 'bg-v2-green-surface',
  FAILED: 'bg-v2-red-surface',
  PENDING: 'bg-v2-btn-secondary dark:bg-v2-surface-tertiary',
};

const STATUS_ICON: Record<PriceAlertDeliveryStatus, { name: IconName; class: string } | null> = {
  DELIVERED: { name: 'CheckCircle', class: 'text-v2-green' },
  FAILED: { name: 'Attention', class: 'text-red-400' },
  PENDING: null,
};

const SETTINGS_HREF = '/account?tab=settings';

export function PriceAlertHistoryRow({ trigger }: PriceAlertHistoryRowProps) {
  const t = useTranslations('priceAlerts');
  const tHist = useTranslations('priceAlerts.history');
  const tFail = useTranslations('priceAlerts.history.failureReason');
  const format = useFormatter();
  const { connect: connectTelegram } = useTelegramConnect();

  const isYes = trigger.side === 'YES';
  const triggeredAt = new Date(trigger.triggeredAt);
  const dateText = format.dateTime(triggeredAt, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });

  const statusLabels: Record<PriceAlertDeliveryStatus, string> = {
    DELIVERED: tHist('delivered'),
    FAILED: tHist('failed'),
    PENDING: tHist('pending'),
  };

  const failureContent =
    trigger.deliveryStatus === 'FAILED' && trigger.failureReason
      ? renderFailureReason(tFail, trigger.failureReason, connectTelegram)
      : null;

  const icon = STATUS_ICON[trigger.deliveryStatus];
  const pillContent = (
    <>
      {icon && <Icon name={icon.name} size={12} className={icon.class} />}
      {statusLabels[trigger.deliveryStatus]}
    </>
  );
  const pillClass = cn(
    'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs leading-[115%] font-normal text-v2-text-primary',
    STATUS_PILL_BG[trigger.deliveryStatus],
    failureContent && 'cursor-pointer',
  );

  return (
    <div className="col-span-full grid grid-cols-subgrid items-center gap-x-2 rounded-2xl px-2 py-4">
      {/* Market Info — outcome + event */}
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-center gap-1">
          <span className="truncate text-xs leading-4 font-normal text-v2-text-primary">
            {trigger.displayText}
          </span>
          <span
            className={cn(
              'shrink-0 rounded-lg px-2 py-0.5 text-xs leading-4 font-normal',
              isYes ? 'bg-v2-green-surface text-v2-green' : 'bg-v2-red-surface text-red-400',
            )}
          >
            {isYes ? t('side.yes') : t('side.no')}
          </span>
        </div>
        {trigger.eventSlug ? (
          <Link
            href={`/event/${trigger.eventSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-[12px] leading-[150%] text-v2-text-secondary transition-colors hover:text-v2-text-primary hover:underline"
          >
            {trigger.eventName}
          </Link>
        ) : (
          <span className="truncate text-[12px] leading-[150%] text-v2-text-secondary">
            {trigger.eventName}
          </span>
        )}
      </div>

      {/* Date */}
      <span className="whitespace-nowrap text-right text-xs leading-4 font-normal text-v2-text-primary">
        {dateText}
      </span>

      {/* Status + channel */}
      <div className="flex flex-col items-end gap-1">
        {failureContent ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className={pillClass}>
                {pillContent}
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="end"
              showArrow={false}
              style={{ zIndex: Z_OVERLAY_MAX }}
              className="w-fit max-w-[280px] rounded-[10px] border-0 bg-[#222628] p-4 text-xs leading-4 font-normal tracking-[-0.01em] text-pretty text-white"
            >
              {failureContent}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className={pillClass}>{pillContent}</span>
        )}
        <span className="whitespace-nowrap text-[12px] leading-[115%] text-v2-text-secondary">
          {t(`channels.${trigger.deliveryChannel}`)}
        </span>
      </div>
    </div>
  );
}

function renderFailureReason(
  t: ReturnType<typeof useTranslations<'priceAlerts.history.failureReason'>>,
  reason: PriceAlertFailureReason,
  onConnectTelegram: () => void,
): ReactNode {
  const link = (chunks: ReactNode) => (
    <Link
      href={SETTINGS_HREF}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-300 underline underline-offset-2"
    >
      {chunks}
    </Link>
  );
  const bot = (chunks: ReactNode) => (
    <button
      type="button"
      onClick={onConnectTelegram}
      className="cursor-pointer text-blue-300 underline underline-offset-2"
    >
      {chunks}
    </button>
  );

  switch (reason) {
    case 'DISABLED':
      return t.rich('disabled', { link });
    case 'NO_CONTACT':
      return t.rich('noContact', { link });
    case 'TG_BOT_BLOCKED':
      return t.rich('tgBotBlocked', { bot });
    case 'TG_CHAT_NOT_FOUND':
      return t.rich('tgChatNotFound', { bot });
    case 'TG_UNAVAILABLE':
      return t('tgUnavailable');
    case 'INTERNAL_ERROR':
      return t('internalError');
    case 'INVALID_TARGET':
      return t.rich('invalidTarget', { link });
  }
}
